import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { formatDate } from "../../lib/dateFormat";

// Inline SVG icons to avoid lucide-react dependency
const Icons = {
  Users: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Shield: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  Dumbbell: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  ),
  Trophy: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  ),
  Search: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  ArrowLeft: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  ),
  Eye: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  Edit: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
};

type MemberRole = "club_admin" | "coach" | "athlete";

interface Member {
  id: string;
  name: string;
  email: string;
  role: MemberRole;
  team?: string;
  joined_date: string;
  status: "active" | "inactive";
  points?: number;
}

export default function ClubMembers() {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const [roleFilter, setRoleFilter] = useState<MemberRole | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Mock club data
  const clubName = "Steaua Academy";

  // Mock members
  const allMembers: Member[] = [
    {
      id: "1",
      name: "Andrei Ionescu",
      email: "andrei@steaua.ro",
      role: "club_admin",
      joined_date: "2024-01-15",
      status: "active",
    },
    {
      id: "2",
      name: "Maria Popescu",
      email: "maria@steaua.ro",
      role: "coach",
      team: "U16 Team A",
      joined_date: "2024-02-20",
      status: "active",
    },
    {
      id: "3",
      name: "Ion Vasilescu",
      email: "ion.v@steaua.ro",
      role: "coach",
      team: "U18 Team B",
      joined_date: "2024-03-10",
      status: "active",
    },
    {
      id: "4",
      name: "Elena Stanescu",
      email: "elena@athlete.ro",
      role: "athlete",
      team: "U16 Team A",
      joined_date: "2024-05-01",
      status: "active",
      points: 2345,
    },
    {
      id: "5",
      name: "Mihai Dumitrescu",
      email: "mihai@athlete.ro",
      role: "athlete",
      team: "U16 Team A",
      joined_date: "2024-06-15",
      status: "active",
      points: 1890,
    },
    {
      id: "6",
      name: "Ana Georgescu",
      email: "ana@athlete.ro",
      role: "athlete",
      team: "U18 Team B",
      joined_date: "2024-07-20",
      status: "inactive",
      points: 456,
    },
  ];

  const filteredMembers = allMembers.filter((member) => {
    const matchesRole = roleFilter === "all" || member.role === roleFilter;
    const matchesSearch =
      searchQuery === "" ||
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const getRoleBadge = (role: MemberRole) => {
    switch (role) {
      case "club_admin": return "bg-purple-100 text-purple-700 border border-purple-200";
      case "coach": return "bg-blue-100 text-blue-700 border border-blue-200";
      case "athlete": return "bg-emerald-100 text-emerald-700 border border-emerald-200";
    }
  };

  const getRoleLabel = (role: MemberRole) => {
    return t(`roles.${role}`);
  };

  const memberCounts = {
    total: allMembers.length,
    club_admin: allMembers.filter(m => m.role === "club_admin").length,
    coach: allMembers.filter(m => m.role === "coach").length,
    athlete: allMembers.filter(m => m.role === "athlete").length,
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 px-4 py-2 mb-4 text-slate-600 hover:text-slate-800 hover:bg-white rounded-xl transition-colors"
          >
            <Icons.ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </button>
          <h1 className="text-3xl font-bold text-slate-800">{clubName}</h1>
          <p className="text-slate-500 mt-1">Gestionează toți membrii, antrenorii și sportivii clubului</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-50 rounded-xl">
                <Icons.Users className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total membri</p>
                <p className="text-2xl font-bold text-slate-800">{memberCounts.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-xl">
                <Icons.Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Administratori</p>
                <p className="text-2xl font-bold text-slate-800">{memberCounts.club_admin}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <Icons.Dumbbell className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Antrenori</p>
                <p className="text-2xl font-bold text-slate-800">{memberCounts.coach}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <Icons.Trophy className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Sportivi</p>
                <p className="text-2xl font-bold text-slate-800">{memberCounts.athlete}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Icons.Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder={t("common.search") + " după nume sau email..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              />
            </div>

            <div className="flex gap-2">
              {["all", "club_admin", "coach", "athlete"].map((role) => (
                <button
                  key={role}
                  onClick={() => setRoleFilter(role as any)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    roleFilter === role
                      ? "bg-teal-600 text-white shadow-md"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {role === "all" ? "Toate" : getRoleLabel(role as MemberRole)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Membru
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Echipă
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Puncte
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Înscris
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-slate-400">
                      <Icons.Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Niciun membru găsit</p>
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-teal-700 font-semibold text-sm">
                              {member.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-slate-800">{member.name}</div>
                            <div className="text-sm text-slate-500">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadge(member.role)}`}>
                          {getRoleLabel(member.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {member.team || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.points !== undefined ? (
                          <span className="font-semibold text-amber-600">{member.points.toLocaleString()}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {formatDate(new Date(member.joined_date))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            member.status === "active"
                              ? "bg-green-100 text-green-700 border border-green-200"
                              : "bg-slate-100 text-slate-500 border border-slate-200"
                          }`}
                        >
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
                            <Icons.Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">
                            <Icons.Edit className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
